import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, Loader2 } from 'lucide-react';
import { horoscopeService, HoroscopeEntry } from '../services/horoscopeApi';

const SIGNS = [
  { name: 'Áries',      emoji: '♈', dates: '21/03 – 19/04' },
  { name: 'Touro',      emoji: '♉', dates: '20/04 – 20/05' },
  { name: 'Gêmeos',     emoji: '♊', dates: '21/05 – 20/06' },
  { name: 'Câncer',     emoji: '♋', dates: '21/06 – 22/07' },
  { name: 'Leão',       emoji: '♌', dates: '23/07 – 22/08' },
  { name: 'Virgem',     emoji: '♍', dates: '23/08 – 22/09' },
  { name: 'Libra',      emoji: '♎', dates: '23/09 – 22/10' },
  { name: 'Escorpião',  emoji: '♏', dates: '23/10 – 21/11' },
  { name: 'Sagitário',  emoji: '♐', dates: '22/11 – 21/12' },
  { name: 'Capricórnio',emoji: '♑', dates: '22/12 – 19/01' },
  { name: 'Aquário',    emoji: '♒', dates: '20/01 – 18/02' },
  { name: 'Peixes',     emoji: '♓', dates: '19/02 – 20/03' },
];

const STORAGE_KEY = 'astralyz_horoscope_sign';

const HoroscopePage: React.FC = () => {
  const savedSign = localStorage.getItem(STORAGE_KEY) || SIGNS[0].name;
  const [selectedSign, setSelectedSign] = useState(savedSign);
  const [horoscope, setHoroscope] = useState<HoroscopeEntry | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchHoroscope(selectedSign);
  }, [selectedSign]);

  const fetchHoroscope = async (sign: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await horoscopeService.getToday(sign);
      setHoroscope(data);
    } catch {
      setError('Não foi possível carregar o horóscopo agora.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSign = (name: string) => {
    localStorage.setItem(STORAGE_KEY, name);
    setSelectedSign(name);
  };

  const active = SIGNS.find(s => s.name === selectedSign) || SIGNS[0];

  const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-6 border-b border-white/20">
        <Link to="/dashboard" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </Link>
        <h1 className="text-xl font-mystical font-bold">Horóscopo do Dia</h1>
        <div className="w-20" />
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">

        {/* Grade de signos */}
        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-2 mb-8">
          {SIGNS.map(s => (
            <button
              key={s.name}
              onClick={() => handleSelectSign(s.name)}
              className="flex flex-col items-center gap-1 py-2 px-1 rounded-xl transition-all"
              style={
                selectedSign === s.name
                  ? { background: 'rgba(139,92,246,0.35)', border: '1px solid rgba(139,92,246,0.7)' }
                  : { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }
              }
            >
              <span className="text-xl">{s.emoji}</span>
              <span className="text-[10px] text-white/70 leading-tight text-center">{s.name}</span>
            </button>
          ))}
        </div>

        {/* Card do horóscopo */}
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedSign}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(124,58,237,0.2) 0%, rgba(59,130,246,0.15) 100%)',
              border: '1px solid rgba(139,92,246,0.3)',
            }}
          >
            {/* Cabeçalho do signo */}
            <div className="px-6 pt-6 pb-4 flex items-center gap-4 border-b border-white/10">
              <span className="text-5xl">{active.emoji}</span>
              <div>
                <h2 className="text-2xl font-mystical font-bold text-white">{active.name}</h2>
                <p className="text-white/50 text-sm">{active.dates}</p>
              </div>
              {horoscope && (
                <div className="ml-auto flex items-center gap-1.5 text-white/40 text-xs">
                  <Calendar size={13} />
                  {formatDate(horoscope.date)}
                </div>
              )}
            </div>

            {/* Conteúdo */}
            <div className="px-6 py-5 min-h-[160px] flex items-center">
              {loading ? (
                <div className="flex items-center gap-3 text-white/60 mx-auto">
                  <Loader2 size={20} className="animate-spin" />
                  <span className="text-sm">Consultando os astros…</span>
                </div>
              ) : error ? (
                <p className="text-red-300 text-sm text-center w-full">{error}</p>
              ) : horoscope ? (
                <p className="text-white/85 text-sm leading-relaxed whitespace-pre-line text-justify">{horoscope.content}</p>
              ) : null}
            </div>
          </motion.div>
        </AnimatePresence>

        <p className="text-center text-white/25 text-xs mt-6">
          Horóscopo com carinho por Aurora Lyz · atualizado diariamente
        </p>
      </div>
    </div>
  );
};

export default HoroscopePage;

